import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getMyPoints } from '@/api/auth';

export interface PointRecord {
  id: string;
  action: string;
  points: number;
  createdAt: string;
  targetId?: string;
  targetTitle?: string;
  manualBy?: string; // 管理员手动增加时记录
  type?: 'auto' | 'manual' | 'levelup'; // 记录类型
}

export interface PointsData {
  total: number;
  history: PointRecord[];
}

export interface LevelInfo {
  level: number;
  name: string;
  title: string; // 修仙称号
  minPoints: number;
  maxPoints: number;
  color: string;
  icon: string;
  nextLevelName?: string;
}

export interface LevelSetting {
  name: string;
  title: string;
  minPoints: number;
  color: string;
  icon: string;
}

const UNKNOWN_LEVEL_INFO: LevelInfo = {
  level: 0,
  name: '未配置',
  title: '待配置',
  minPoints: 0,
  maxPoints: Infinity,
  color: '#94a3b8',
  icon: 'ri-question-line',
};

export function usePoints(options?: { lazy?: boolean }) {
  const { isLoggedIn, user } = useAuth();
  const [data, setData] = useState<PointsData>({ total: 0, history: [] });
  const [levelInfoState, setLevelInfoState] = useState<LevelInfo>(() => UNKNOWN_LEVEL_INFO);
  const [levelsState, setLevelsState] = useState<LevelInfo[]>([]);
  const [nextLevelState, setNextLevelState] = useState<LevelInfo | null>(null);
  const [progressState, setProgressState] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lazy = options?.lazy ?? false;

  const parseLevelInfo = useCallback((level: unknown): LevelInfo | null => {
    if (!level || typeof level !== 'object') return null;
    const raw = level as Record<string, unknown>;
    const levelNum = Number(raw.level);
    const minPoints = Number(raw.minPoints);
    const rawMaxPoints = raw.maxPoints;
    const maxPoints = rawMaxPoints === null ? Infinity : Number(rawMaxPoints);
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    const title = typeof raw.title === 'string' ? raw.title.trim() : '';
    const color = typeof raw.color === 'string' ? raw.color.trim() : '';
    const icon = typeof raw.icon === 'string' ? raw.icon.trim() : '';
    const nextLevelName = typeof raw.nextLevelName === 'string' ? raw.nextLevelName : undefined;
    const maxPointsIsOpenEnded = rawMaxPoints === null;
    const maxPointsIsNumeric = Number.isFinite(maxPoints);
    if (
      !Number.isFinite(levelNum)
      || !Number.isFinite(minPoints)
      || (!maxPointsIsOpenEnded && !maxPointsIsNumeric)
      || !name
      || !title
      || !color
      || !icon
    ) {
      return null;
    }
    return {
      level: Math.trunc(levelNum),
      name,
      title,
      minPoints: Math.trunc(minPoints),
      maxPoints: maxPoints === Infinity ? Infinity : Math.trunc(maxPoints),
      color,
      icon,
      nextLevelName,
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setData({ total: 0, history: [] });
      setLevelInfoState(UNKNOWN_LEVEL_INFO);
      setNextLevelState(null);
      setProgressState(0);
      setLevelsState([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getMyPoints();
      const total = Number(res.total);
      if (!Number.isFinite(total)) {
        throw new Error('积分总数格式错误');
      }

      if (!Array.isArray(res.ledger)) {
        throw new Error('灵石明细响应格式错误');
      }
      const ledger = res.ledger;
      const mappedHistory: PointRecord[] = ledger.map((item: any) => {
        const actionLabel = item.actionLabel ?? item.action_label ?? item.actionKey ?? item.action_key ?? '灵石变动';
        const pointsDelta = Number(item.pointsDelta ?? item.points_delta ?? 0);
        const createdAt = item.createdAt ?? item.created_at ?? '';
        const targetTitle = item.targetTitle ?? item.target_title ?? undefined;
        return {
          id: `ledger_${item.id}`,
          action: actionLabel,
          points: Number.isFinite(pointsDelta) ? pointsDelta : 0,
          createdAt: String(createdAt || ''),
          targetTitle: typeof targetTitle === 'string' ? targetTitle : undefined,
        };
      });
      setData({
        total: Math.trunc(total),
        history: mappedHistory,
      });

      if (!Array.isArray(res.levels) || res.levels.length === 0) {
        throw new Error('等级配置响应缺失');
      }
      const rawLevels = res.levels;
      const parsedLevels = rawLevels
          .map((item) => parseLevelInfo(item))
          .filter((item): item is LevelInfo => Boolean(item));
      if (parsedLevels.length !== rawLevels.length) {
        throw new Error('等级配置响应格式错误');
      }
      setLevelsState(parsedLevels);

      const current = parseLevelInfo(res.currentLevel);
      if (!current) {
        throw new Error('当前等级响应缺失');
      }
      setLevelInfoState(current);

      if (res.nextLevel === null) {
        setNextLevelState(null);
      } else {
        const next = parseLevelInfo(res.nextLevel);
        if (!next) {
          throw new Error('下一等级响应格式错误');
        }
        setNextLevelState(next);
      }

      const progressRaw = Number(res.progressToNext);
      if (!Number.isFinite(progressRaw)) {
        throw new Error('等级进度响应格式错误');
      }
      setProgressState(Math.max(0, Math.min(100, progressRaw)));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user points:', err);
      setData({ total: 0, history: [] });
      setLevelsState([]);
      setLevelInfoState(UNKNOWN_LEVEL_INFO);
      setNextLevelState(null);
      setProgressState(0);
      setError(err instanceof Error ? err.message : '灵石数据加载失败');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, parseLevelInfo, user?.id]);

  useEffect(() => {
    if (!lazy) {
      void refresh();
    }
  }, [refresh, lazy]);

  useEffect(() => {
    const handlePointsUpdate = () => void refresh();
    window.addEventListener('wanxiang_points_updated', handlePointsUpdate);
    window.addEventListener('wanxiang_settings_updated', handlePointsUpdate);
    return () => {
      window.removeEventListener('wanxiang_points_updated', handlePointsUpdate);
      window.removeEventListener('wanxiang_settings_updated', handlePointsUpdate);
    };
  }, [refresh]);

  return {
    points: data.total,
    history: data.history,
    levels: levelsState,
    refresh,
    loading,
    error,
    levelInfo: levelInfoState,
    progressToNext: progressState,
    nextLevel: nextLevelState,
  };
}
